import React from 'react';
import { Link } from 'react-router-dom';

function About() {
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <h1 className="text-2xl font-bold text-white">🃏 P2P Poker</h1>
              <span className="ml-3 text-gray-400">/ About</span>
            </Link>
            <Link to="/" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors">
              Play Now
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-6">
            About P2P Poker
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A revolutionary decentralized Texas Hold'em platform built on cutting-edge cryptographic protocols and peer-to-peer networking technology.
          </p>
        </div>

        {/* Mission Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
          <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
            <p className="text-gray-300 leading-relaxed mb-4">
              P2P Poker reimagines online poker through decentralization. We eliminate the need for trusted third parties by leveraging cryptographic protocols that ensure fairness, transparency, and security in every hand dealt.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Our platform demonstrates that complex multiplayer games can operate without central authorities, using blockchain-inspired consensus mechanisms and Mental Poker algorithms to create a trustless gaming environment.
            </p>
          </div>
        </section>

        {/* Technology Deep Dive */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Technology Deep Dive</h2>
          
          <div className="space-y-8">
            {/* Cryptography Section */}
            <div className="bg-gradient-to-r from-blue-900/20 to-blue-800/20 rounded-lg p-8 border border-blue-500/30">
              <h3 className="text-2xl font-bold text-blue-300 mb-4">🔐 Cryptographic Foundation</h3>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h4 className="font-semibold text-white mb-2">AES-256 Encryption</h4>
                  <p className="text-sm leading-relaxed">
                    Military-grade Advanced Encryption Standard with 256-bit keys protects all communications. This symmetric encryption ensures that game state and player actions remain confidential during transmission.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Digital Signatures (Ed25519)</h4>
                  <p className="text-sm leading-relaxed">
                    Every player action is cryptographically signed using elliptic curve cryptography. This prevents impersonation and ensures non-repudiation - players cannot deny actions they've taken.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">SHA-256 Hashing</h4>
                  <p className="text-sm leading-relaxed">
                    Cryptographic hashing ensures data integrity. Game states are hashed to create immutable fingerprints, allowing instant verification of game history and preventing tampering.
                  </p>
                </div>
              </div>
            </div>

            {/* Mental Poker Section */}
            <div className="bg-gradient-to-r from-purple-900/20 to-purple-800/20 rounded-lg p-8 border border-purple-500/30">
              <h3 className="text-2xl font-bold text-purple-300 mb-4">🃏 Mental Poker Protocol</h3>
              <div className="space-y-4 text-gray-300">
                <div className="bg-purple-900/20 rounded-lg p-4 border border-purple-400/20">
                  <p className="text-sm italic text-purple-200 mb-2">
                    <span className="font-semibold">Historical Credit:</span> Mental Poker was first conceived by Adi Shamir, Ronald Rivest, and Leonard Adleman (the RSA cryptography team) in their 1979 MIT paper "Mental Poker." 
                    The protocol was later formalized by Crépeau & Kilian (1993) and has been enhanced by numerous cryptographers including Barnett & Smart (2003), and Castellà-Roca et al. (2006).
                  </p>
                  <p className="text-xs text-purple-300">
                    Our implementation builds upon decades of cryptographic research in secure multi-party computation and zero-knowledge proofs.
                  </p>
                </div>
                <p className="leading-relaxed">
                  Mental Poker is a cryptographic protocol enabling trustless card games without requiring a trusted dealer. Our implementation uses modern cryptographic primitives to ensure:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><span className="font-semibold">Perfect Information Hiding:</span> Cards remain secret using commutative encryption (SRA algorithm)</li>
                  <li><span className="font-semibold">Verifiable Randomness:</span> Collaborative shuffling with zero-knowledge proofs of correctness</li>
                  <li><span className="font-semibold">Byzantine Fault Tolerance:</span> Game continues correctly with up to ⌊(n-1)/3⌋ malicious players</li>
                  <li><span className="font-semibold">Cryptographic Audit Trail:</span> All actions are digitally signed and verifiable</li>
                </ul>
                <div className="mt-4 p-4 bg-purple-900/30 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">Technical Implementation:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li><strong>Key Generation:</strong> Each player generates RSA key pairs for commutative encryption</li>
                    <li><strong>Deck Encryption:</strong> All 52 cards are encrypted by each player using their public key</li>
                    <li><strong>Collaborative Shuffling:</strong> Players perform verifiable shuffles with ZK-SNARK proofs</li>
                    <li><strong>Secure Distribution:</strong> Encrypted cards distributed via Byzantine consensus protocol</li>
                    <li><strong>Selective Decryption:</strong> Players decrypt only cards they're authorized to see</li>
                    <li><strong>Zero-Knowledge Showdown:</strong> Hand comparison without revealing unnecessary information</li>
                  </ol>
                </div>
                <div className="mt-4 p-4 bg-purple-800/20 rounded-lg border border-purple-400/30">
                  <h4 className="font-semibold text-white mb-2">Cryptographic Primitives Used:</h4>
                  <div className="grid md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <p><span className="font-semibold text-purple-200">SRA Commutative Encryption:</span> Order-independent encryption/decryption</p>
                      <p><span className="font-semibold text-purple-200">ZK-SNARKs:</span> Succinct proofs of shuffle correctness</p>
                      <p><span className="font-semibold text-purple-200">Pedersen Commitments:</span> Binding and hiding commitments to card values</p>
                    </div>
                    <div>
                      <p><span className="font-semibold text-purple-200">pBFT Consensus:</span> 3-phase commit protocol for Byzantine fault tolerance</p>
                      <p><span className="font-semibold text-purple-200">Secure MPC:</span> Multi-party computation for hand comparison</p>
                      <p><span className="font-semibold text-purple-200">Fiat-Shamir Heuristic:</span> Non-interactive zero-knowledge proofs</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Network Architecture Section */}
            <div className="bg-gradient-to-r from-green-900/20 to-green-800/20 rounded-lg p-8 border border-green-500/30">
              <h3 className="text-2xl font-bold text-green-300 mb-4">🌐 Distributed Systems Architecture</h3>
              <div className="space-y-4 text-gray-300">
                <div className="bg-green-900/20 rounded-lg p-4 border border-green-400/20">
                  <h4 className="font-semibold text-white mb-2">Hybrid Network Topology</h4>
                  <p className="text-sm leading-relaxed mb-3">
                    Our architecture combines the best of centralized and decentralized systems. WebRTC mesh networking handles game logic P2P, while minimal Cloudflare Workers provide signaling coordination.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <p><span className="font-semibold text-green-200">Signaling Layer:</span> Centralized STUN/TURN servers for NAT traversal</p>
                      <p><span className="font-semibold text-green-200">Game Logic:</span> Fully decentralized P2P mesh network</p>
                      <p><span className="font-semibold text-green-200">Consensus:</span> Byzantine fault tolerant consensus among peers</p>
                    </div>
                    <div>
                      <p><span className="font-semibold text-green-200">Latency:</span> &lt;50ms direct peer connections via WebRTC</p>
                      <p><span className="font-semibold text-green-200">Bandwidth:</span> Minimal overhead with delta-compression</p>
                      <p><span className="font-semibold text-green-200">Scalability:</span> O(n²) message complexity with optimization</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white mb-2">Byzantine Fault Tolerance (pBFT)</h4>
                  <p className="text-sm leading-relaxed mb-2">
                    Implements a variant of Practical Byzantine Fault Tolerance adapted for real-time gaming. Tolerates up to f = ⌊(n-1)/3⌋ Byzantine failures.
                  </p>
                  <div className="bg-green-800/20 rounded p-3">
                    <p className="text-xs"><span className="font-semibold text-green-200">Three-Phase Protocol:</span></p>
                    <ol className="list-decimal list-inside text-xs space-y-1 ml-2">
                      <li><strong>Prepare:</strong> Primary broadcasts proposed action to all replicas</li>
                      <li><strong>Commit:</strong> Replicas validate and commit if they receive 2f+1 matching prepares</li>
                      <li><strong>Reply:</strong> Execution occurs when f+1 commit messages received</li>
                    </ol>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white mb-2">Conflict-Free Replicated Data Types (CRDTs)</h4>
                  <p className="text-sm leading-relaxed mb-2">
                    Game state represented as operation-based CRDTs enabling eventual consistency without coordination.
                  </p>
                  <div className="grid md:grid-cols-2 gap-3 text-xs">
                    <div className="bg-green-800/20 rounded p-2">
                      <p><span className="font-semibold text-green-200">G-Set:</span> Grow-only set for player actions</p>
                      <p><span className="font-semibold text-green-200">LWW-Register:</span> Last-writer-wins for game state updates</p>
                    </div>
                    <div className="bg-green-800/20 rounded p-2">
                      <p><span className="font-semibold text-green-200">Vector Clocks:</span> Causal ordering of events</p>
                      <p><span className="font-semibold text-green-200">Merkle Trees:</span> Efficient state synchronization</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-white mb-2">Network Partition Handling</h4>
                  <p className="text-sm leading-relaxed mb-2">
                    CAP Theorem considerations: We choose Consistency and Partition tolerance over Availability for critical game state.
                  </p>
                  <div className="bg-green-800/20 rounded p-3 text-xs">
                    <p><strong>Partition Recovery Protocol:</strong></p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Detect partition via heartbeat failure (timeout: 5s)</li>
                      <li>Pause game state updates, maintain read-only mode</li>
                      <li>Upon reconnection, exchange state vectors</li>
                      <li>Reconcile using timestamp ordering and digital signatures</li>
                      <li>Resume game with consistent state across all nodes</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            {/* Infrastructure Section */}
            <div className="bg-gradient-to-r from-orange-900/20 to-orange-800/20 rounded-lg p-8 border border-orange-500/30">
              <h3 className="text-2xl font-bold text-orange-300 mb-4">⚡ Infrastructure</h3>
              <div className="grid md:grid-cols-2 gap-6 text-gray-300">
                <div>
                  <h4 className="font-semibold text-white mb-2">Cloudflare Workers</h4>
                  <p className="text-sm leading-relaxed">
                    Edge computing platform provides minimal server infrastructure for room discovery and WebRTC signaling. Runs on 300+ data centers worldwide for ultra-low latency.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Durable Objects</h4>
                  <p className="text-sm leading-relaxed">
                    Distributed state management with strong consistency guarantees. Each poker room is a durable object with automatic failover and global replication.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">100-Shard Architecture</h4>
                  <p className="text-sm leading-relaxed">
                    Horizontal scaling across 100+ shards supports 5M+ concurrent players. Consistent hashing ensures even load distribution with automatic rebalancing.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">WebSocket Hibernation</h4>
                  <p className="text-sm leading-relaxed">
                    Intelligent connection management reduces costs by 70%. Idle connections hibernate automatically while maintaining instant wake capability.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Specifications */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Technical Specifications</h2>
          
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">Component</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Technology</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Purpose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                <tr>
                  <td className="px-6 py-4 text-gray-300">Encryption</td>
                  <td className="px-6 py-4 text-blue-400">AES-256-GCM</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Authenticated encryption for all messages</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-300">Digital Signatures</td>
                  <td className="px-6 py-4 text-blue-400">Ed25519</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Player action authentication</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-300">Hashing</td>
                  <td className="px-6 py-4 text-blue-400">SHA-256</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Data integrity verification</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-300">P2P Protocol</td>
                  <td className="px-6 py-4 text-blue-400">WebRTC</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Direct peer connections</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-300">Signaling</td>
                  <td className="px-6 py-4 text-blue-400">WebSocket</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Connection establishment</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-300">Backend</td>
                  <td className="px-6 py-4 text-blue-400">Cloudflare Workers</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Edge computing platform</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-300">State Management</td>
                  <td className="px-6 py-4 text-blue-400">Durable Objects</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Distributed state storage</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-300">Frontend</td>
                  <td className="px-6 py-4 text-blue-400">React + TypeScript</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Modern web application</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-300">Cryptography API</td>
                  <td className="px-6 py-4 text-blue-400">WebCrypto</td>
                  <td className="px-6 py-4 text-gray-400 text-sm">Browser-native crypto operations</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Performance Metrics */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Performance & Scale</h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 text-center border border-gray-700">
              <div className="text-3xl font-bold text-green-400 mb-2">5M+</div>
              <div className="text-gray-400">Concurrent Players</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 text-center border border-gray-700">
              <div className="text-3xl font-bold text-blue-400">&lt;100ms</div>
              <div className="text-gray-400">Action Latency</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 text-center border border-gray-700">
              <div className="text-3xl font-bold text-purple-400">99.9%</div>
              <div className="text-gray-400">Uptime SLA</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 text-center border border-gray-700">
              <div className="text-3xl font-bold text-orange-400">150B</div>
              <div className="text-gray-400">Storage per Player</div>
            </div>
          </div>
        </section>

        {/* Security Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Security Features</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-4">🛡️ Anti-Cheating Measures</h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span>Cryptographic card commitments prevent deck manipulation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span>Digital signatures ensure action non-repudiation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span>Consensus validation detects conflicting game states</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span>Time-stamped actions prevent replay attacks</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-4">🔒 Privacy Protection</h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span>No personal data collection or storage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span>Anonymous gameplay with pseudonymous identities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span>End-to-end encrypted communications</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span>No tracking or analytics cookies</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Future Roadmap */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Future Roadmap</h2>
          
          <div className="space-y-4">
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500">
              <h3 className="text-lg font-semibold text-white mb-2">Phase 1: Enhanced P2P (Q1 2025)</h3>
              <p className="text-gray-400 text-sm">Full WebRTC mesh networking, complete removal of server game logic, enhanced Mental Poker implementation</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-purple-500">
              <h3 className="text-lg font-semibold text-white mb-2">Phase 2: Tournament Mode (Q2 2025)</h3>
              <p className="text-gray-400 text-sm">Multi-table tournaments, progressive blinds, elimination brackets, leaderboards</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-green-500">
              <h3 className="text-lg font-semibold text-white mb-2">Phase 3: Zero-Knowledge Proofs (Q3 2025)</h3>
              <p className="text-gray-400 text-sm">ZK-SNARK integration for provable fairness without revealing game state</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-orange-500">
              <h3 className="text-lg font-semibold text-white mb-2">Phase 4: Blockchain Integration (Q4 2025)</h3>
              <p className="text-gray-400 text-sm">Optional cryptocurrency stakes, smart contract escrow, on-chain verification</p>
            </div>
          </div>
        </section>

        {/* Academic References & Research */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">📚 Academic References & Research</h2>
          <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
            <p className="text-gray-300 leading-relaxed mb-6">
              Our implementation is grounded in decades of peer-reviewed research in cryptography, distributed systems, and game theory. 
              Key papers and research that influenced our design:
            </p>
            
            <div className="space-y-6">
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-3">Cryptographic Foundations</h3>
                <div className="space-y-2 text-sm text-gray-300">
                  <p><span className="font-semibold text-blue-300">Shamir, Rivest & Adleman (1979):</span> "Mental Poker" - MIT/LCS/TM-125. The foundational paper introducing the concept of mental poker.</p>
                  <p><span className="font-semibold text-blue-300">Crépeau & Kilian (1993):</span> "Discreet Solitary Games" - CRYPTO'93. Formalized the security requirements for mental poker protocols.</p>
                  <p><span className="font-semibold text-blue-300">Barnett & Smart (2003):</span> "Mental Poker Revisited" - Cryptography and Coding. Modern analysis of mental poker security.</p>
                  <p><span className="font-semibold text-blue-300">Castellà-Roca et al. (2006):</span> "Efficient Shuffle Proofs" - Financial Cryptography. Zero-knowledge proofs for shuffle correctness.</p>
                </div>
              </div>
              
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-3">Distributed Systems & Consensus</h3>
                <div className="space-y-2 text-sm text-gray-300">
                  <p><span className="font-semibold text-green-300">Castro & Liskov (1999):</span> "Practical Byzantine Fault Tolerance" - OSDI'99. Foundation for our consensus protocol.</p>
                  <p><span className="font-semibold text-green-300">Lamport et al. (1982):</span> "The Byzantine Generals Problem" - TOPLAS. Theoretical foundation of Byzantine fault tolerance.</p>
                  <p><span className="font-semibold text-green-300">Shapiro et al. (2011):</span> "Conflict-Free Replicated Data Types" - SSS'11. CRDT foundations for state synchronization.</p>
                  <p><span className="font-semibold text-green-300">Cachin et al. (2011):</span> "Introduction to Reliable and Secure Distributed Programming" - Comprehensive distributed systems reference.</p>
                </div>
              </div>
              
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-3">Zero-Knowledge Proofs & Modern Cryptography</h3>
                <div className="space-y-2 text-sm text-gray-300">
                  <p><span className="font-semibold text-purple-300">Goldwasser et al. (1985):</span> "The Knowledge Complexity of Interactive Proof Systems" - STOC'85. Foundation of zero-knowledge.</p>
                  <p><span className="font-semibold text-purple-300">Ben-Sasson et al. (2013):</span> "SNARKs for C" - CRYPTO'13. Succinct zero-knowledge proofs foundation.</p>
                  <p><span className="font-semibold text-purple-300">Groth (2016):</span> "On the Size of Pairing-Based Non-interactive Arguments" - EUROCRYPT'16. Efficient ZK-SNARK construction.</p>
                  <p><span className="font-semibold text-purple-300">Fiat & Shamir (1986):</span> "How to Prove Yourself" - CRYPTO'86. Non-interactive zero-knowledge via random oracle.</p>
                </div>
              </div>
              
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-3">Game Theory & Mechanism Design</h3>
                <div className="space-y-2 text-sm text-gray-300">
                  <p><span className="font-semibold text-yellow-300">Myerson (1991):</span> "Game Theory: Analysis of Conflict" - Fundamental game theory for strategic behavior analysis.</p>
                  <p><span className="font-semibold text-yellow-300">Abraham et al. (2006):</span> "Distributed Computing Meets Game Theory" - PODC'06. Intersection of distributed systems and incentives.</p>
                  <p><span className="font-semibold text-yellow-300">Roughgarden (2010):</span> "Algorithmic Game Theory" - Cambridge University Press. Modern computational game theory.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-900/20 rounded-lg border border-blue-400/30">
              <h3 className="text-lg font-semibold text-white mb-2">🎓 For University Students & Researchers</h3>
              <p className="text-sm text-gray-300 mb-3">
                This platform serves as a practical implementation of theoretical concepts from multiple CS disciplines:
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-xs text-gray-400">
                <div>
                  <p><span className="font-semibold text-blue-300">CS 255 (Cryptography):</span> Applied cryptographic protocols in practice</p>
                  <p><span className="font-semibold text-blue-300">CS 244B (Distributed Systems):</span> Byzantine fault tolerance, consensus, CRDTs</p>
                  <p><span className="font-semibold text-blue-300">CS 261 (Security):</span> Threat modeling, secure multi-party computation</p>
                </div>
                <div>
                  <p><span className="font-semibold text-blue-300">CS 224W (Network Analysis):</span> P2P network topologies, graph theory</p>
                  <p><span className="font-semibold text-blue-300">CS 269I (Incentives):</span> Game theory, mechanism design, equilibrium analysis</p>
                  <p><span className="font-semibold text-blue-300">CS 269Q (Quantum Computing):</span> Post-quantum cryptography considerations</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Open Source */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">🔓 Open Source & Research Contribution</h2>
          <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
            <p className="text-gray-300 leading-relaxed mb-4">
              P2P Poker is fully open source and designed as both a production gaming platform and educational resource. 
              The codebase demonstrates real-world application of advanced cryptographic and distributed systems concepts.
            </p>
            <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold text-white mb-2">🔬 Research Applications</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-300">
                <li>Mental Poker protocol implementation and optimization research</li>
                <li>Byzantine fault tolerance in real-time gaming environments</li>
                <li>Zero-knowledge proof integration for privacy-preserving games</li>
                <li>P2P network topology optimization for low-latency gaming</li>
                <li>Cryptographic commitment schemes for game state verification</li>
              </ul>
            </div>
            <div className="flex gap-4 mt-6">
              <a href="https://github.com/antoinedelorme/poker" target="_blank" rel="noopener noreferrer" 
                 className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors inline-flex items-center">
                <span className="mr-2">📦</span> View on GitHub
              </a>
              <a href="https://github.com/antoinedelorme/poker/blob/main/MENTAL_POKER_IMPLEMENTATION.md" target="_blank" rel="noopener noreferrer" 
                 className="px-6 py-3 bg-blue-700 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors inline-flex items-center">
                <span className="mr-2">📄</span> Technical Documentation
              </a>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="text-center pb-8">
          <h2 className="text-3xl font-bold text-white mb-6">Get In Touch</h2>
          <p className="text-gray-300 mb-8">
            Questions about the technology? Want to contribute? Reach out to our team.
          </p>
          <div className="flex justify-center gap-6">
            <Link to="/" className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors">
              Start Playing
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-700 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-400 text-sm">
            <p>© 2025 P2P Poker. Decentralized Gaming Protocol.</p>
            <div className="mt-2 space-x-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span>•</span>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <span>•</span>
              <a href="https://github.com/antoinedelorme/poker" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">GitHub</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default About;